import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import type { QueryData } from "@supabase/supabase-js";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AlertCircle, SquareArrowOutUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

const getRegressionSelect = () =>
  supabase
    .from("test_data_run_regressions")
    .select("id, viewed, created_at, success_classification_run_result_id, failure_classification_run_result_id");

type Regression = QueryData<ReturnType<typeof getRegressionSelect>>[number];

const RegressionsPopover = () => {
  const navigate = useNavigate();

  const { regressions, markViewed, markAllViewed } = useRegressionsWithRealtime();
  const hasUnviewedRegressions = regressions.some((reg) => !reg.viewed);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative flex items-center" title="Regressions">
          <AlertCircle className="h-5 w-5" />
          {hasUnviewedRegressions && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-4 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Regressions</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllViewed.mutate()}
              disabled={!hasUnviewedRegressions || markAllViewed.isPending}
              aria-label="Mark all viewed"
            >
              Mark all viewed
            </Button>
          </div>
          {regressions && regressions.length > 0 ? (
            <div className="max-h-[240px] overflow-y-auto">
              <ul className="space-y-2">
                {regressions.map((reg) => (
                  <li key={reg.id} className="flex flex-col gap-1 text-sm relative">
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">{dayjs(reg.created_at).tz().format("DD/MM HH:mm")}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          markViewed.mutate(reg.id, {
                            onSuccess: () => {
                              navigate(`/test-data/runs/${reg.success_classification_run_result_id}`);
                            },
                          });
                        }}
                        aria-label="View Success Run"
                        disabled={markViewed.isPending}
                      >
                        <SquareArrowOutUpRight className="h-5 w-5 text-green-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          markViewed.mutate(reg.id, {
                            onSuccess: () => {
                              navigate(`/test-data/runs/${reg.failure_classification_run_result_id}`);
                            },
                          });
                        }}
                        aria-label="View Failure Run"
                        disabled={markViewed.isPending}
                      >
                        <SquareArrowOutUpRight className="h-5 w-5 text-red-500" />
                      </Button>
                      {/* Red dot for unread (viewed = false) */}
                      {reg.viewed === false && (
                        <span className="absolute right-0 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-red-500" />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">No unviewed regressions.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RegressionsPopover;

const useRegressionsWithRealtime = () => {
  const [regressions, setRegressions] = useState<Regression[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch initial data
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    getRegressionSelect()
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (!isMounted) return;
        if (error) {
          setError(error);
          setIsLoading(false);
        } else {
          setRegressions(data || []);
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Subscribe to realtime changes
  useEffect(() => {
    const channel = supabase
      .channel("test_data_run_regressions-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "test_data_run_regressions",
        },
        (payload) => {
          setRegressions((prev) => {
            if (!prev) return prev;
            const { eventType, new: newRow, old: oldRow } = payload;
            if (eventType === "INSERT") {
              // Add new regression to the top
              return [newRow as Regression, ...prev].slice(0, 50);
            } else if (eventType === "UPDATE") {
              // Replace the updated regression
              return prev.map((reg) => (reg.id === newRow.id ? { ...reg, ...newRow } : reg));
            } else if (eventType === "DELETE") {
              // Remove the deleted regression
              return prev.filter((reg) => reg.id !== oldRow.id);
            }
            return prev;
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Mutation to mark regression as viewed
  const markViewed = useMutation({
    mutationFn: async (regressionId: string) => {
      const { error } = await supabase
        .from("test_data_run_regressions")
        .update({ viewed: true })
        .eq("id", regressionId);
      if (error) throw error;
    },
  });

  // Mutation to mark all regressions as viewed
  const markAllViewed = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("test_data_run_regressions").update({ viewed: true }).eq("viewed", false);
      if (error) throw error;
      // Optionally update local state immediately
      setRegressions((prev) => prev.map((reg) => ({ ...reg, viewed: true })));
    },
  });

  return { regressions, isLoading, error, markViewed, markAllViewed };
};
